import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Eye,
  Calendar,
  User,
  Phone,
  Mail,
  Sparkles,
  X,
  ExternalLink
} from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import useToast from "../hooks/useToast";
import { getCareerApplications } from "../services/careerApplicationService";

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function CareerApplications() {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const { showErrorToast } = useToast();

  const loadApplications = async (pageArg = pagination.page, limitArg = pagination.limit) => {
    setIsLoading(true);
    const response = await getCareerApplications({
      page: pageArg,
      limit: limitArg
    });

    if (response?.success) {
      setApplications(response.data.applications || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load applications");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadApplications(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openViewModal = (application) => {
    setSelectedApplication(application);
  };

  const closeViewModal = () => {
    setSelectedApplication(null);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Talent Module"
        title="Career Applications"
        description="View and manage job applications received from the frontend website form."
        badge={`${pagination.total} Applications`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Inbound Applications</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              Standardized listing of all user applications with detail view and resume links.
            </p>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading applications...
            </div>
          ) : applications.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Applicant</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Position</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Phone</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Date</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {applications.map((app) => (
                        <tr key={app.id} className="border-t border-slate-100 align-top hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-xs font-bold text-white uppercase">
                                {getInitials(`${app.firstname} ${app.lastname}`)}
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-semibold text-panel-ink truncate max-w-[150px]">{app.firstname} {app.lastname}</span>
                                <span className="text-xs text-panel-muted truncate max-w-[150px]">{app.email}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm font-medium text-panel-ink leading-relaxed">
                            {app.category}
                          </td>
                          <td className="px-5 py-4 text-sm text-panel-muted">
                            {app.phonenumber}
                          </td>
                          <td className="px-5 py-4 text-sm text-panel-muted whitespace-nowrap">
                            {formatDate(app.created_at)}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <Button type="button" size="sm" variant="outline" onClick={() => openViewModal(app)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                  <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                  <span className="font-semibold text-panel-ink">{pagination.total}</span> applications
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-panel-muted">
                    Rows
                    <select
                      className="h-10 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-panel-ink outline-none"
                      value={pagination.limit}
                      onChange={(event) =>
                        setPagination((current) => ({
                          ...current,
                          page: 1,
                          limit: Number(event.target.value)
                        }))
                      }
                    >
                      {[10, 20, 50].map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() =>
                        setPagination((current) => ({
                          ...current,
                          page: current.page - 1
                        }))
                      }
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Prev
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                      {pageNumbers.map((item, index) =>
                        item === "..." ? (
                          <span key={`ellipsis-${index}`} className="px-1 text-sm font-medium text-panel-muted">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={item}
                            type="button"
                            size="sm"
                            variant={pagination.page === item ? "default" : "outline"}
                            onClick={() =>
                              setPagination((current) => ({
                                ...current,
                                page: item
                              }))
                            }
                          >
                            {item}
                          </Button>
                        )
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() =>
                        setPagination((current) => ({
                          ...current,
                          page: current.page + 1
                        }))
                      }
                    >
                      Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No applications yet</p>
              <p className="mt-2 text-sm text-panel-muted">
                Applications submitted via the website career form will appear here.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedApplication ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="max-h-[85dvh] w-full max-w-2xl overflow-y-auto rounded-[32px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-panel-ink truncate max-w-[400px]">
                    Application Detail
                  </h2>
                  <p className="text-sm text-panel-muted">Received on {formatDate(selectedApplication.created_at)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <div className="flex items-center gap-2 text-primary">
                    <User className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Applicant Name</span>
                  </div>
                  <p className="text-panel-ink font-semibold">{selectedApplication.firstname} {selectedApplication.lastname}</p>
                </div>

                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <div className="flex items-center gap-2 text-primary">
                    <FileText className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Position Applied</span>
                  </div>
                  <p className="text-panel-ink font-semibold">{selectedApplication.category}</p>
                </div>

                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100 min-w-0">
                  <div className="flex items-center gap-2 text-primary">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Email Address</span>
                  </div>
                  <a href={`mailto:${selectedApplication.email}`} className="text-panel-ink font-semibold hover:text-primary transition-colors break-all block">
                    {selectedApplication.email}
                  </a>
                </div>

                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100 min-w-0">
                  <div className="flex items-center gap-2 text-primary">
                    <Phone className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Phone Number</span>
                  </div>
                  <p className="text-panel-ink font-semibold">{selectedApplication.phonenumber}</p>
                </div>
              </div>

              <div className="space-y-3 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100 relative">
                <div className="flex items-center gap-2 text-primary">
                  <FileText className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Resume / Document</span>
                </div>
                <div className="flex items-center justify-between bg-white/60 p-5 rounded-2xl border border-white">
                  <div className="truncate max-w-[400px]">
                    <p className="text-sm font-medium text-panel-ink truncate">
                      {selectedApplication.document_path.split('/').pop()}
                    </p>
                    <p className="text-xs text-panel-muted truncate">
                      {selectedApplication.document_path}
                    </p>
                  </div>
                  <a 
                    href={selectedApplication.document_path} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View
                  </a>
                </div>
              </div>

              <div className="pt-2">
                <Button type="button" className="w-full h-12 rounded-2xl text-base font-bold shadow-panel-sm" onClick={closeViewModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
